import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { BlogContent } from '@/components/blog/BlogContent';
import { Comments } from '@/components/blog/Comments';
import { useBlogPost } from '@/hooks/useBlogPost';
import { Link } from 'react-router-dom';

export const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { post, loading, error } = useBlogPost(slug || '');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-neon-green animate-pulse">Loading post...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <AlertCircle className="w-12 h-12 text-red-500" />
        <div className="text-red-500">Failed to load post</div>
        <Link 
          to="/blog"
          className="flex items-center space-x-2 px-4 py-2 bg-neon-green/20 text-neon-green rounded-lg hover:bg-neon-green/30"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Blog</span>
        </Link>
      </div>
    );
  }

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <Link 
        to="/blog"
        className="inline-flex items-center space-x-2 text-gray-400 hover:text-neon-green mb-8"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Blog</span>
      </Link>

      {post.cover_image && (
        <motion.div 
          className="mb-8 rounded-lg overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <img 
            src={post.cover_image} 
            alt={post.title}
            className="w-full h-[400px] object-cover"
          />
        </motion.div>
      )}

      <BlogContent
        title={post.title}
        content={post.content}
        date={post.published_at || post.created_at}
        author={post.author.name}
        category={post.category}
        tags={post.tags}
      />

      <div className="mt-12">
        <Comments postId={post.id} />
      </div>
    </div>
  );
};